import Vue from 'vue';
import { mapGetters } from 'vuex';
import { guid } from '@/mixins/guid';
import { TheMask } from 'vue-the-mask';
import tooltip from '@/mixins/components/tooltip';
import dropsLoader from '@/mixins/components/drops-loader';

export default Vue.component('mfa', {
  template: require('./template.html'),
  name: 'mfa',
  computed: {
    ...mapGetters(['currentUser']),
    allowEnable() {
      // this would be if in disabled state
      const fullNumber = `+1${this.phone}`.length === 12;
      const enabled = this.mfaEnabled;
      return fullNumber && !enabled && this.verified && !this.initialCodeSent;
    },
    allowDisable() {
      // this would be if in enabled state
      return this.mfaEnabled;
    },
    allowVerify() {
      const fullNumber = `+1${this.phone}`.length === 12;
      const changed = this.phone !== this.initials.phone;
      const caseOne = fullNumber && !this.verified && !this.initialCodeSent;
      const caseTwo = changed && fullNumber && this.verified && !this.initialCodeSent;
      return caseOne || caseTwo;
    },
    mfaAction() {
      return this.mfaEnabled ? 'Disable' : 'Enable';
    },
  },
  mixins: [guid],
  components: {
    dropsLoader,
    TheMask,
    tooltip,
  },
  data() {
    return {
      loading: false,
      countError: false,
      countErrorMsg: 'You must enter a full US phone number. ',
      editMfa: false,
      verified: false,
      mfaEnabled: false,
      phone: '',
      verifycode: '',
      initials: {
        phone: '',
        verified: false,
        mfaEnabled: false,
      },
      loaders: {
        getCode: false,
        verifyCode: false,
      },
      initialCodeSent: false,
      confirmDisable: false,
    };
  },
  watch: {
    editMfa(edit) {
      if (edit) {
        this.setInitialValues();
      }
    },
  },
  methods: {
    async setInitialValues() {
      this.verified = this.currentUser.phone_number_verified === 'true';
      this.phone = (this.currentUser.phone || '').replace(/^\+1/, '');
      this.mfaEnabled = this.currentUser.mfaEnabled;
      this.initials.verified = this.verified;
      this.initials.phone = this.phone;
      this.initials.mfaEnabled = this.mfaEnabled;
    },
    async getIntialCode() {
      const fullNumber = `+1${this.phone}`;
      this.countError = false;
      if (fullNumber.length !== 12) {
        this.countError = this.countErrorMsg;
        return;
      }
      if (this.allowVerify) {
        this.initialCodeSent = false;
        this.loaders.getCode = true;
        const notif = {
          id: this.guid(),
          type: 'success',
          message: 'MFA code sent!',
        };
        try {
          // update attr in cognito
          await this._cognitoUpdateAttrs({ attrs: [{
            Name: 'phone_number',
            Value: fullNumber,
          }] });
          // send the initial code only if mfa is disabled
          await this._timeBuffer(250);
          await this._cognitoGetInitialMFAcode();
          this.initialCodeSent = true;
          this.verified = false;
          notif.time = 5000;
        } catch (e) {
          notif.type = 'error';
          notif.message = `${e.message || e}. This has been reported.`;
          await this._reportError({
            error: e,
            fn: 'getIntialCode - MFA',
          });
        } finally {
          this.loaders.getCode = false;
          this.$store.dispatch('addNotification', notif);
        }
      }
    },
    async verifyCode() {
      this.$validator.validate('verifycode', this.verifycode).then(async (result) => {
        if (result) {
          this.loaders.verifyCode = true;
          const notif = {
            id: this.guid(),
            type: 'success',
            message: 'Phone number verified!',
          };
          try {
            await this._cognitoVerifyInitialMFA({ code: this.verifycode });
            this.$store.dispatch('setGlobalValue', {
              item: this.currentUser,
              key: 'phone_number_verified',
              value: 'true',
            });
            this.verified = true;
            this.initialCodeSent = false;
            this.verifycode = '';
            this.initials.verified = this.verified;
            // only update phone in state when fully approved
            this.$store.dispatch('setGlobalValue', {
              item: this.currentUser,
              key: 'phone',
              value: this.phone,
            });
            this.initials.phone = this.phone;
          } catch (e) {
            notif.type = 'error';
            notif.message = e.message || e;
          } finally {
            this.loaders.verifyCode = false;
            this.$store.dispatch('addNotification', notif);
          }
        }
      });
    },
    toggleMFA() {
      this.$validator.validateAll().then(async (result) => {
        const fullNumber = `+1${this.phone}`;
        this.countError = false;
        if (fullNumber.length !== 12) {
          this.countError = this.countErrorMsg;
          return;
        }
        if (result) {
          const value = this.mfaAction !== 'Disable';
          this.loading = true;
          const notif = {
            id: this.guid(),
            type: 'success',
            message: 'MFA successfully updated!',
          };
          try {
            await this._cognitoToggleMFA({ enabled: value });
            await this.$lightning([{
              name: 'updateUser',
              payload: { attr: 'mfaEnabled', value },
            }]);
            this.$store.dispatch('setGlobalValue', {
              item: this.currentUser,
              key: 'mfaEnabled',
              value,
            });
            this.initials.mfaEnabled = value;
            this.mfaEnabled = value;
            this.editMfa = false;
          } catch (e) {
            notif.type = 'error';
            notif.message = `${e.message || e}. This has been reported.`;
            await this._reportError({
              error: e,
              fn: 'saveChanges - MFA',
            });
          } finally {
            this.loading = false;
            this.confirmDisable = false;
            this.$store.dispatch('addNotification', notif);
          }
        }
      });
    },
    cancelEdit() {
      this.editMfa = !this.editMfa;
      if (!this.editMfa) {
        this.verified = this.initials.verified;
        this.phone = this.initials.phone;
        this.mfaEnabled = this.initials.mfaEnabled;
        this.verifycode = '';
        this.initialCodeSent = false;
      }
    },
    checkLength() {
      this.countError = `+1${this.phone}`.length !== 12 ? this.countErrorMsg : false;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/admin/account/security/mfa/index.js